import { capitalize, debounce, isNil, omitBy } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import useLocalStorage from 'use-local-storage';
import usePrevious from './use-previous';
import deepEqual from 'deep-equal';

export function useStickyFilters<T extends object>(
  key: string,
  initialRequest: T,
  callback: (request: T) => void | Promise<void>,
  callbackDeps: any[] = [],
) {
  const [request, setRequest] = useState<T>(initialRequest);
  const [, setLocalSearchParams] = useLocalStorage<Record<string, any>>(`search_${key}`, {});
  const preCallbackDeps = usePrevious(callbackDeps);
  const preRequest = usePrevious(request);

  // Parse property
  const parsePropPrefix = (key: string, prefix?: string) => (prefix ? `${prefix}${capitalize(key)}` : key);

  // Parse request to params
  const parseRequestToParams = (t: T, prefix?: string): [string, any][] => {
    const object = omitBy(t, isNil);

    return Object.keys(object).flatMap((key) => {
      const value = object[key];

      if (object[key].constructor === Object) {
        return parseRequestToParams(value, parsePropPrefix(key, prefix));
      }

      return [[parsePropPrefix(key, prefix), value]];
    });
  };

  // Debounce callback to prevent multiple requests
  // This should be called from any component only when the rows change their properties (like status, name, etc)
  const fetchCallback = useCallback(
    debounce(async () => {
      if (!deepEqual(request, preRequest) || !deepEqual(callbackDeps, preCallbackDeps)) {
        callback(request);
      }
    }, 500),
    [request, ...callbackDeps],
  );

  // Run the debounce callback when the search params change
  useEffect(() => {
    fetchCallback();
  }, [request, ...callbackDeps]);

  // Update the search params and store them when the request changes
  useEffect(() => {
    const newQueryParams = parseRequestToParams(request);

    setLocalSearchParams(newQueryParams);
  }, [request]);

  return { request, setRequest };
}

export default useStickyFilters;
